import { useEffect, useState } from "react";
import type { DealSummary } from "@edge/shared";
import type { CrmTaskCreateInput } from "../../hooks/useCrmTaskActions";

export interface CrmTaskCreateDialogProps {
  /** Prefill — from a watchtower alert OR a chat `propose_crm_task_create` directive. */
  initial: {
    subject: string;
    description?: string;
    /** YYYY-MM-DD. If omitted, dialog defaults to today. */
    due_date?: string;
    /** If omitted, dialog defaults to Normal. */
    priority?: "Highest" | "High" | "Normal" | "Low" | "Lowest";
    deal_id?: string;
  };
  /** Deal options for the linked-deal picker. */
  deals: DealSummary[];
  onCreate: (input: CrmTaskCreateInput) => Promise<void>;
  onCancel: () => void;
}

/** "YYYY-MM-DD" — today + offsetDays, in Pacific time. */
function todayPlus(offsetDays: number): string {
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
  }).format(new Date());
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/**
 * Form dialog for creating a new Zoho Task from a Watchtower alert. Uses
 * the same modal-backdrop visual treatment as WriteConfirm and the same
 * boundary rules: Escape or CANCEL only — backdrop click does not dismiss.
 */
export function CrmTaskCreateDialog({
  initial,
  deals,
  onCreate,
  onCancel,
}: CrmTaskCreateDialogProps) {
  const [subject, setSubject] = useState(initial.subject);
  const [description, setDescription] = useState(initial.description ?? "");
  const [dueDate, setDueDate] = useState(initial.due_date ?? todayPlus(0));
  const [dealId, setDealId] = useState(initial.deal_id ?? "");
  const [priority, setPriority] = useState<
    "Highest" | "High" | "Normal" | "Low" | "Lowest"
  >(initial.priority ?? "Normal");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !running) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, running]);

  const handleCreate = async () => {
    const trimmed = subject.trim();
    if (!trimmed) {
      setError("subject_required");
      return;
    }
    setError(null);
    setRunning(true);
    try {
      await onCreate({
        subject: trimmed,
        description: description.trim() || undefined,
        due_date: dueDate || undefined,
        deal_id: dealId || undefined,
        priority,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "write_failed");
      setRunning(false);
    }
    // Parent unmounts on success.
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card crm-create-card">
        <div className="write-confirm-title">Create Zoho CRM task</div>
        <div className="crm-create-body">
          <label className="crm-create-field">
            <span className="crm-create-label">Subject</span>
            <input
              className="crm-create-input"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
            />
          </label>
          <label className="crm-create-field">
            <span className="crm-create-label">Description</span>
            <textarea
              className="crm-create-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </label>
          <div className="crm-create-row">
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Due date</span>
              <input
                className="crm-create-input"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>
            <label className="crm-create-field crm-create-field-half">
              <span className="crm-create-label">Priority</span>
              <select
                className="crm-create-input"
                value={priority}
                onChange={(e) =>
                  setPriority(
                    e.target.value as
                      | "Highest"
                      | "High"
                      | "Normal"
                      | "Low"
                      | "Lowest",
                  )
                }
              >
                <option value="Highest">Highest</option>
                <option value="High">High</option>
                <option value="Normal">Normal</option>
                <option value="Low">Low</option>
                <option value="Lowest">Lowest</option>
              </select>
            </label>
          </div>
          <label className="crm-create-field">
            <span className="crm-create-label">Linked deal</span>
            <select
              className="crm-create-input"
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
            >
              <option value="">(no deal)</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={running}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm"
            onClick={handleCreate}
            disabled={running}
          >
            {running ? "WRITING…" : "CREATE IN ZOHO"}
          </button>
        </div>
      </div>
    </div>
  );
}
